import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/expenses';
const TREASURY_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury/accounts';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';

const CATEGORIES = [
  'Office Supplies', 'Printing & Stationery', 'Venue Hire', 'Refreshments',
  'Transport Advance', 'Communication', 'Bank Charges', 'Welfare',
  'Repairs & Maintenance', 'Bereavement', 'Other'
];

function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [recoveries, setRecoveries] = useState({});
  const [payingRecoveryId, setPayingRecoveryId] = useState(null);
  const [payForm, setPayForm] = useState({ amount_paid: '', account_id: '', payment_date: new Date().toISOString().split('T')[0], receipt_ref: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    account_id: '', amount: '', description: '', category: '',
    expense_date: new Date().toISOString().split('T')[0],
    receipt_ref: '', expense_type: 'regular', member_id: ''
  });
  // Tracks whether "Other" is selected in the main form's category
  // dropdown, so the free-text input can be shown independently of
  // form.category itself (which holds the FINAL value — the typed
  // text, not the literal word "Other"). Kept separate from form.category
  // because once the user types something like "Vet Fees", that value
  // won't match any <option>, so the <select> alone can't represent
  // "custom category mode" — this flag does.
  const [isOtherCategory, setIsOtherCategory] = useState(false);
  // Same idea, but for the inline table-row edit form. Only one row can
  // be edited at a time (editingId), so a single shared flag is enough.
  const [editIsOtherCategory, setEditIsOtherCategory] = useState(false);
  const readOnly = isChairmanReadOnly();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchExpenses();
    axios.get(TREASURY_API, { headers }).then(r => setAccounts(r.data)).catch(() => {});
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const fetchExpenses = () => {
    axios.get(API, { headers }).then(r => setExpenses(r.data)).catch(() => {});
  };

  const loadRecoveries = (expenseId) => {
    if (expandedId === expenseId) { setExpandedId(null); return; }
    setExpandedId(expenseId);
    axios.get(`${API}/${expenseId}/recoveries`, { headers })
      .then(r => setRecoveries(prev => ({ ...prev, [expenseId]: r.data })))
      .catch(() => {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      const res = await axios.post(API, form, { headers });
      let msg = 'Expense recorded';
      if (res.data.per_member_amount) {
        msg = `Collective advance recorded — UGX ${Math.round(res.data.per_member_amount).toLocaleString()} per member across ${res.data.members_count} members`;
      }
      setSuccess(msg);
      setShowForm(false);
      setForm({ account_id: '', amount: '', description: '', category: '', expense_date: new Date().toISOString().split('T')[0], receipt_ref: '', expense_type: 'regular', member_id: '' });
      setIsOtherCategory(false);
      fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record expense');
    }
  };

  const saveEdit = async (id) => {
    setError(''); setSuccess('');
    try {
      await axios.patch(`${API}/${id}`, editData, { headers });
      setSuccess('Expense updated');
      setEditingId(null);
      setEditIsOtherCategory(false);
      fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const deleteExpense = async (id, description) => {
    if (!window.confirm(`Delete "${description}"? This reverses the treasury debit AND any member recovery payments already made. This cannot be undone.`)) return;
    setError(''); setSuccess('');
    try {
      const res = await axios.delete(`${API}/${id}`, { headers });
      setSuccess(res.data.message);
      fetchExpenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const recordPayment = async (recoveryId, expenseId) => {
    setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API}/recoveries/${recoveryId}/pay`, payForm, { headers });
      setSuccess(res.data.message);
      setPayingRecoveryId(null);
      setPayForm({ amount_paid: '', account_id: '', payment_date: new Date().toISOString().split('T')[0], receipt_ref: '' });
      fetchExpenses();
      axios.get(`${API}/${expenseId}/recoveries`, { headers })
        .then(r => setRecoveries(prev => ({ ...prev, [expenseId]: r.data })));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    }
  };

  // FIXED: Total Expenses previously summed every row regardless of type,
  // including collective/recoverable advances (transport hired for a
  // bereavement, later recovered from members). Those are receivables —
  // a temporary transfer out of treasury, tracked per member, never a
  // real cost — and should never appear in "Total Expenses." Only
  // 'regular' rows are genuine, non-recoverable expenses.
  const trueExpenses = expenses.filter(e => e.expense_type === 'regular');
  const totalExpenses = trueExpenses.reduce((s, e) => s + parseFloat(e.amount), 0);
  const advanceExpenses = expenses.filter(e => e.expense_type !== 'regular');
  const totalAdvances = advanceExpenses.reduce((s, e) => s + parseFloat(e.amount), 0);
  const totalRecovered = advanceExpenses.reduce((s, e) => s + parseFloat(e.amount_recovered || 0), 0);
  const totalPending = advanceExpenses.reduce((s, e) => s + parseFloat(e.balance_to_recover || 0), 0);

  const displayExpenses = tab === 'advances' ? advanceExpenses : expenses;

  const statusColor = (s) => ({
    not_applicable: '#95a5a6', pending: '#e74c3c',
    partially_recovered: '#f39c12', recovered: '#2ecc71'
  }[s] || '#95a5a6');

  const typeLabel = (t) => ({
    regular: 'Regular', recoverable: '↩ Individual Advance', collective: '↩ Collective Advance'
  }[t] || t);

  const typeBg = (t) => ({
    regular: '#7f8c8d', recoverable: '#8e44ad', collective: '#c0392b'
  }[t] || '#7f8c8d');

  return (
    <div style={{ padding: '20px' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#2c3e50', margin: 0 }}>Expenses</h2>
        {!readOnly && <button onClick={() => setShowForm(!showForm)} style={btnStyle('#2c3e50')}>
          {showForm ? 'Cancel' : '+ Record Expense'}
        </button>}
      </div>

      {error && <div style={alertStyle('#e74c3c')}>{error}<span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success}<span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={kpiCard('#e74c3c')}><div style={kpiLabel}>Total Expenses</div><div style={{ ...kpiValue, fontSize: '18px' }}>UGX {totalExpenses.toLocaleString()}</div></div>
        <div style={kpiCard('#8e44ad')}><div style={kpiLabel}>Transport Advances</div><div style={{ ...kpiValue, fontSize: '18px' }}>UGX {totalAdvances.toLocaleString()}</div></div>
        <div style={kpiCard('#27ae60')}><div style={kpiLabel}>Recovered</div><div style={{ ...kpiValue, fontSize: '18px' }}>UGX {totalRecovered.toLocaleString()}</div></div>
        <div style={kpiCard('#f39c12')}><div style={kpiLabel}>Pending Recovery</div><div style={{ ...kpiValue, fontSize: '18px' }}>UGX {totalPending.toLocaleString()}</div></div>
      </div>

      {!readOnly && showForm && (
        <form onSubmit={handleSubmit} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', width: '100%', color: '#2c3e50' }}>Record Expense</h3>

          <div style={{ width: '100%', display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {[
              { value: 'regular', label: 'Regular Expense', color: '#2c3e50' },
              { value: 'recoverable', label: '↩ Individual Advance', color: '#8e44ad' },
              { value: 'collective', label: '↩ Collective Advance (All Members)', color: '#c0392b' },
            ].map(opt => (
              <button key={opt.value} type="button"
                onClick={() => setForm({ ...form, expense_type: opt.value, member_id: '' })}
                style={{ ...btnStyle(form.expense_type === opt.value ? opt.color : '#ecf0f1'), color: form.expense_type === opt.value ? 'white' : '#2c3e50', fontSize: '13px' }}>
                {opt.label}
              </button>
            ))}
          </div>

          {form.expense_type === 'recoverable' && (
            <div style={{ width: '100%', background: '#f4ecf7', border: '1px solid #8e44ad', borderRadius: '6px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px', color: '#6e2fa0' }}>
              ↩ Individual advance — one member will pay back the full amount.
            </div>
          )}
          {form.expense_type === 'collective' && (
            <div style={{ width: '100%', background: '#fdf3f3', border: '1px solid #c0392b', borderRadius: '6px', padding: '10px 14px', marginBottom: '10px', fontSize: '12px', color: '#c0392b' }}>
              ↩ Collective advance — amount will be split equally across ALL active members. Each member pays back their share individually.
              {form.amount && members.length > 0 && (
                <strong> Per member: UGX {Math.round(parseFloat(form.amount || 0) / members.filter(m => m.status === 'active').length).toLocaleString()}</strong>
              )}
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>Account to Debit *</label>
            <select style={inputStyle} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} required>
              <option value=''>-- Select --</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name} — UGX {Number(a.balance).toLocaleString()}</option>)}
            </select>
          </div>

          {form.expense_type === 'recoverable' && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Member (beneficiary) *</label>
              <select style={inputStyle} value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })} required>
                <option value=''>-- Select Member --</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>)}
              </select>
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>Category</label>
            <select style={inputStyle} value={isOtherCategory ? 'Other' : form.category}
              onChange={e => {
                if (e.target.value === 'Other') {
                  setIsOtherCategory(true);
                  setForm({ ...form, category: '' });
                } else {
                  setIsOtherCategory(false);
                  setForm({ ...form, category: e.target.value });
                }
              }}>
              <option value=''>-- Select --</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {isOtherCategory && (
              <input
                style={{ ...inputStyle, marginTop: '6px' }}
                placeholder='Type the category name'
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                required
                autoFocus
              />
            )}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Amount (UGX) *</label>
            <input type='number' style={inputStyle} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
          </div>

          <div style={{ ...fieldStyle, minWidth: '280px' }}>
            <label style={labelStyle}>Description *</label>
            <input style={inputStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder={form.expense_type === 'collective' ? 'e.g. Transport to Kisembo burial — Masaka' : 'Description'} required />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Date</label>
            <input type='date' style={inputStyle} value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Receipt Ref</label>
            <input style={inputStyle} value={form.receipt_ref} onChange={e => setForm({ ...form, receipt_ref: e.target.value })} />
          </div>

          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type='submit' style={btnStyle(form.expense_type === 'collective' ? '#c0392b' : form.expense_type === 'recoverable' ? '#8e44ad' : '#2c3e50')}>
              {form.expense_type === 'collective' ? 'Record Collective Advance' : form.expense_type === 'recoverable' ? 'Record Individual Advance' : 'Save Expense'}
            </button>
            <button type='button' onClick={() => { setShowForm(false); setIsOtherCategory(false); }} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
        {[['all', `All Expenses (${expenses.length})`], ['advances', `Transport Advances (${advanceExpenses.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
            background: tab === key ? '#2c3e50' : 'transparent',
            color: tab === key ? 'white' : '#7f8c8d', borderRadius: '4px 4px 0 0'
          }}>{label}</button>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#2c3e50', color: 'white' }}>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Amount (UGX)</th>
              <th style={thStyle}>Account</th>
              <th style={thStyle}>Recovery</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayExpenses.length === 0 && (
              <tr><td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No expenses yet</td></tr>
            )}
            {displayExpenses.map((e, i) => (
              <>
                {editingId === e.id ? (
                  <tr key={`edit-${e.id}`} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                    <td style={tdStyle}>
                      <input type="date" value={editData.expense_date} onChange={x => setEditData({ ...editData, expense_date: x.target.value })} style={inlineInput} />
                    </td>
                    <td style={tdStyle}>{typeLabel(e.expense_type)}</td>
                    <td style={tdStyle}>
                      <select
                        value={editIsOtherCategory ? 'Other' : (editData.category || '')}
                        onChange={x => {
                          if (x.target.value === 'Other') {
                            setEditIsOtherCategory(true);
                            setEditData({ ...editData, category: '' });
                          } else {
                            setEditIsOtherCategory(false);
                            setEditData({ ...editData, category: x.target.value });
                          }
                        }}
                        style={inlineInput}>
                        <option value=''>--</option>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {editIsOtherCategory && (
                        <input
                          style={{ ...inlineInput, marginTop: '4px' }}
                          placeholder='Type category'
                          value={editData.category || ''}
                          onChange={x => setEditData({ ...editData, category: x.target.value })}
                          autoFocus
                        />
                      )}
                    </td>
                    <td style={tdStyle}><input value={editData.description} onChange={x => setEditData({ ...editData, description: x.target.value })} style={inlineInput} /></td>
                    <td style={tdStyle}>{Number(e.amount).toLocaleString()}</td>
                    <td style={tdStyle}>{e.account_name}</td>
                    <td style={tdStyle}>—</td>
                    <td style={tdStyle}>
                      {!readOnly && <button onClick={() => saveEdit(e.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                      <button onClick={() => { setEditingId(null); setEditIsOtherCategory(false); }} style={smBtn('#95a5a6')}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={`row-${e.id}`} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}>{e.expense_date ? e.expense_date.slice(0, 10) : '—'}</td>
                    <td style={tdStyle}>
                      <span style={{ background: typeBg(e.expense_type), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                        {typeLabel(e.expense_type)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px', color: '#666' }}>{e.category || '—'}</td>
                    <td style={tdStyle}>{e.description}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold', color: '#e74c3c' }}>-{Number(e.amount).toLocaleString()}</td>
                    <td style={tdStyle}>{e.account_name}</td>
                    <td style={tdStyle}>
                      {e.expense_type === 'regular' ? (
                        <span style={{ color: '#95a5a6', fontSize: '12px' }}>N/A</span>
                      ) : (
                        <div>
                          <span style={{ background: statusColor(e.recovery_status), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                            {e.recovery_status?.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          {e.expense_type === 'collective' && (
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                              {e.recovered_count}/{e.recovery_count} members paid
                            </div>
                          )}
                          {parseFloat(e.balance_to_recover) > 0 && (
                            <div style={{ fontSize: '11px', color: '#e74c3c', marginTop: '2px' }}>
                              Balance: UGX {Number(e.balance_to_recover).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {!readOnly && <button onClick={() => {
                          setEditingId(e.id);
                          const currentCategory = e.category || '';
                          const isKnown = CATEGORIES.includes(currentCategory);
                          setEditIsOtherCategory(currentCategory !== '' && !isKnown);
                          setEditData({ description: e.description, receipt_ref: e.receipt_ref || '', category: currentCategory, expense_date: e.expense_date?.slice(0, 10) || '' });
                        }} style={smBtn('#e67e22')}>Edit</button>}
                        {e.expense_type !== 'regular' && e.recovery_status !== 'recovered' && (
                          <button onClick={() => loadRecoveries(e.id)} style={smBtn('#8e44ad')}>
                            {expandedId === e.id ? 'Hide' : 'Recover'}
                          </button>
                        )}
                        {!readOnly && <button onClick={() => deleteExpense(e.id, e.description)} style={{ background: '#fde8e8', color: '#c0392b', border: '1px solid #f5c6cb', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                          Delete
                        </button>}
                      </div>
                    </td>
                  </tr>
                )}

                {expandedId === e.id && recoveries[e.id] && (
                  <tr key={`recovery-${e.id}`}>
                    <td colSpan="8" style={{ padding: '0', background: '#f8f0ff' }}>
                      <div style={{ padding: '16px', borderLeft: '4px solid #8e44ad' }}>
                        <div style={{ fontWeight: 'bold', color: '#6e2fa0', marginBottom: '12px', fontSize: '13px' }}>
                          Recovery Tracker — {e.description}
                          {e.expense_type === 'collective' && (
                            <span style={{ marginLeft: '10px', fontWeight: 'normal', color: '#666' }}>
                              UGX {Math.round(parseFloat(e.amount) / (recoveries[e.id]?.length || 1)).toLocaleString()} per member
                            </span>
                          )}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ background: '#6e2fa0', color: 'white' }}>
                              <th style={thStyle}>Member</th>
                              <th style={thStyle}>Amount Owed</th>
                              <th style={thStyle}>Paid</th>
                              <th style={thStyle}>Balance</th>
                              <th style={thStyle}>Status</th>
                              <th style={thStyle}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(recoveries[e.id] || []).map((r, ri) => (
                              <>
                                <tr key={`r-${r.id}`} style={{ background: ri % 2 === 0 ? 'white' : '#faf5ff' }}>
                                  <td style={tdStyle}><strong>{r.full_name}</strong> <span style={{ color: '#95a5a6' }}>{r.member_number}</span></td>
                                  <td style={tdStyle}>{Number(r.amount_owed).toLocaleString()}</td>
                                  <td style={{ ...tdStyle, color: '#27ae60' }}>{Number(r.amount_paid).toLocaleString()}</td>
                                  <td style={{ ...tdStyle, color: r.balance > 0 ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>{Number(r.balance).toLocaleString()}</td>
                                  <td style={tdStyle}>
                                    <span style={{ background: r.status === 'paid' ? '#2ecc71' : r.status === 'partial' ? '#f39c12' : '#e74c3c', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>
                                      {r.status?.toUpperCase()}
                                    </span>
                                  </td>
                                  <td style={tdStyle}>
                                    {!readOnly && r.status !== 'paid' && payingRecoveryId !== r.id && (
                                      <button onClick={() => {
                                        setPayingRecoveryId(r.id);
                                        setPayForm({ amount_paid: r.balance, account_id: e.account_id, payment_date: new Date().toISOString().split('T')[0], receipt_ref: '' });
                                      }} style={smBtn('#27ae60')}>Pay</button>
                                    )}
                                  </td>
                                </tr>
                                {!readOnly && payingRecoveryId === r.id && (
                                  <tr key={`pay-${r.id}`} style={{ background: '#eafaf1' }}>
                                    <td colSpan="6" style={tdStyle}>
                                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                        <div style={fieldStyle}>
                                          <label style={labelStyle}>Amount Paid</label>
                                          <input type="number" style={inlineInput} value={payForm.amount_paid} onChange={x => setPayForm({ ...payForm, amount_paid: x.target.value })} />
                                        </div>
                                        <div style={fieldStyle}>
                                          <label style={labelStyle}>Credit Account *</label>
                                          <select style={inlineInput} value={payForm.account_id} onChange={x => setPayForm({ ...payForm, account_id: x.target.value })}>
                                            <option value=''>-- Select --</option>
                                            {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                                          </select>
                                        </div>
                                        <div style={fieldStyle}>
                                          <label style={labelStyle}>Date</label>
                                          <input type="date" style={inlineInput} value={payForm.payment_date} onChange={x => setPayForm({ ...payForm, payment_date: x.target.value })} />
                                        </div>
                                        <div style={fieldStyle}>
                                          <label style={labelStyle}>Receipt Ref</label>
                                          <input style={inlineInput} value={payForm.receipt_ref} onChange={x => setPayForm({ ...payForm, receipt_ref: x.target.value })} />
                                        </div>
                                        <button onClick={() => recordPayment(r.id, e.id)} style={smBtn('#27ae60')}>Record</button>
                                        <button onClick={() => setPayingRecoveryId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const btnStyle = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' });
const smBtn = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const formStyle = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' };
const labelStyle = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #8e44ad', fontSize: '12px', background: '#faf5ff', width: '100%' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });
const kpiCard = (color) => ({ background: color, color: 'white', padding: '15px 20px', borderRadius: '8px', minWidth: '160px' });
const kpiLabel = { fontSize: '11px', opacity: 0.85, marginBottom: '4px' };
const kpiValue = { fontSize: '24px', fontWeight: 'bold' };

export default Expenses;